// src/pages/Admin/Users/UserManagement.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Plus, 
  UserCheck, 
  UserX, 
  Trash2, 
  Edit3, 
  Shield, 
  ShieldOff,
  Eye,
  CheckCircle,
  XCircle,
  AlertTriangle,
  KeyRound,
  Briefcase
} from 'lucide-react';
import DataTable, { type Column } from '../../../components/Admin/DataTable';
import ConfirmDialog from '../../../components/Admin/ConfirmDialog';
import StaffRoleModal from '../../../components/Admin/StaffRoleModal';
import AddUserModal from '../../../components/Admin/AddUserModal';
import EditUserModal from '../../../components/Admin/EditUserModal';
import adminService, { type User, type DashboardStats } from '../../../services/adminService';
import { useToast } from '../../../hooks/useToast';

const UserManagement: React.FC = () => {
  const location = useLocation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void;
    danger?: boolean;
  }>({ isOpen: false, title: '', message: '', action: () => {}, danger: false });
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const { showToast } = useToast();

  // Determine the view type based on current path
  const viewType = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/admins')) return 'admin';
    if (path.includes('/pending')) return 'pending';
    return 'all';
  }, [location.pathname]);

  const getPageTitle = () => {
    switch (viewType) {
      case 'admin':
        return 'Admin Users';
      case 'pending':
        return 'Pending Verification';
      default:
        return 'User Management';
    }
  };

  const columns: Column<User>[] = [
    {
      key: 'username',
      title: 'User',
      render: (_, record) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center text-white font-medium">
            {record.firstName ? record.firstName[0].toUpperCase() : 'U'}
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{record.firstName} {record.lastName}</p>
            <p className="text-sm text-gray-500">@{record.username}</p>
          </div>
        </div>
      ),
      sortable: true
    },
    {
      key: 'email',
      title: 'Email',
      render: (value) => (
        <span className="text-sm text-gray-900">{value as string}</span>
      ),
      sortable: true
    },
    {
      key: 'isVerified',
      title: 'Status',
      render: (_, record) => (
        <div className="flex flex-col space-y-1">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            record.isVerified
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {record.isVerified ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                Verified
              </>
            ) : (
              <>
                <AlertTriangle className="h-3 w-3 mr-1" />
                Pending
              </>
            )}
          </span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            record.isActive
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}>
            {record.isActive ? (
              <>
                <CheckCircle className="h-3 w-3 mr-1" />
                Active
              </>
            ) : (
              <>
                <XCircle className="h-3 w-3 mr-1" />
                Inactive
              </>
            )}
          </span>
        </div>
      )
    },
    {
      key: 'isStaff',
      title: 'Role',
      // Staff and admin are different roles now, and this column called both "Admin" -
        // isStaff || isAdminUser - so a staff account was indistinguishable from an admin
        // on the one screen where the difference decides what somebody can do.
        //
        // The position lives here rather than in a column of its own. A separate column
        // widened the table enough to need horizontal scrolling, and a role and the job
        // that goes with it read better together than apart.
      render: (_, record) => {
        let role = 'User';
        let colorClass = 'bg-gray-100 text-gray-800';
        let icon = null;

        if (record.isAdminUser) {
          role = 'Admin';
          colorClass = 'bg-orange-100 text-orange-800';
          icon = <Shield className="h-3 w-3 mr-1" />;
        } else if (record.isStaff) {
          role = 'Staff';
          colorClass = 'bg-blue-100 text-blue-800';
          icon = <Briefcase className="h-3 w-3 mr-1" />;
        }

        return (
          <div className="flex flex-col gap-0.5">
            <span className={`inline-flex w-fit items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
              {icon}
              {role}
            </span>
            {record.teamPositionLabel && (
              <span className="text-xs text-gray-600">{record.teamPositionLabel}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'dateJoined',
      title: 'Joined',
      render: (value) => {
        if (!value) return <span className="text-sm text-gray-500">N/A</span>;
        try {
          const date = new Date(value as string);
          if (isNaN(date.getTime())) {
            return <span className="text-sm text-gray-500">Invalid Date</span>;
          }
          return (
            <span className="text-sm text-gray-900">
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          );
        } catch (_error) {
          return <span className="text-sm text-gray-500">Invalid Date</span>;
        }
      },
      sortable: true
    },
    {
      key: 'lastLogin',
      title: 'Last Login',
      render: (value) => {
        if (!value) return <span className="text-sm text-gray-500">Never</span>;
        try {
          const date = new Date(value as string);
          if (isNaN(date.getTime())) {
            return <span className="text-sm text-gray-500">Never</span>;
          }
          return (
            <span className="text-sm text-gray-500">
              {date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </span>
          );
        } catch (_error) {
          return <span className="text-sm text-gray-500">Never</span>;
        }
      },
      sortable: true
    }
  ];

  const actions = [
    {
      title: 'View Details',
      key: 'view',
      render: (record: User) => (
        <Link
          to={`/admin/users/${record.id}`}
          className="text-orange-600 hover:text-orange-900 transition-colors"
        >
          <Eye className="h-4 w-4" />
        </Link>
      )
    },
    {
      title: 'Edit User',
      key: 'edit',
      render: (record: User) => (
        <button
          onClick={() => handleEditUser(record)}
          className="text-blue-600 hover:text-blue-900 transition-colors"
        >
          <Edit3 className="h-4 w-4" />
        </button>
      )
    },
    {
      title: 'Toggle Admin',
      key: 'admin',
      render: (record: User) => (
        <button
          onClick={() => handleToggleAdmin(record)}
          className={`${
            (record.isStaff || record.isAdminUser) ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'
          } transition-colors`}
        >
          {(record.isStaff || record.isAdminUser) ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
        </button>
      )
    },
    {
      title: 'Staff Role',
      key: 'staff',
      render: (record: User) => (
        <button
          onClick={() => handleToggleStaff(record)}
          title={record.isStaff
            ? `Remove the staff role (${record.teamPositionLabel || 'no position'})`
            : 'Give a staff role and a position on the team'}
          aria-label={record.isStaff
            ? `Remove staff role from ${record.email}`
            : `Give ${record.email} a staff role`}
          className={`${record.isStaff ? 'text-orange-600 hover:text-orange-900' : 'text-gray-400 hover:text-gray-700'} transition-colors`}
        >
          <Briefcase className="h-4 w-4" />
        </button>
      )
    },
    {
      title: 'Reset Password',
      key: 'force-reset',
      render: (record: User) => (
        <button
          onClick={() => handleForcePasswordReset(record)}
          title="Invalidate the password, sign them out everywhere, and email a reset code"
          aria-label={`Force a password reset for ${record.email}`}
          className="text-amber-600 hover:text-amber-900 transition-colors"
        >
          <KeyRound className="h-4 w-4" />
        </button>
      )
    },
    {
      title: 'Delete User',
      key: 'delete',
      render: (record: User) => (
        <button
          onClick={() => handleDeleteUser(record)}
          className="text-red-600 hover:text-red-900 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )
    }
  ];

  const bulkActions = [
    {
      label: 'Make Admin',
      key: 'make-admin',
      onClick: handleBulkMakeAdmin
    },
    {
      label: 'Remove Admin',
      key: 'remove-admin',
      onClick: handleBulkRemoveAdmin
    },
    {
      label: 'Delete Users',
      key: 'delete',
      onClick: handleBulkDelete,
      danger: true
    }
  ];

  const loadDashboardStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const stats = await adminService.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Error loading dashboard stats:', error);
      // If dashboard stats fail, we'll fall back to the filtered data for now
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      
      // Determine status filter based on view type
      let status: string | undefined;
      if (viewType === 'admin') {
        status = 'admin';
      } else if (viewType === 'pending') {
        status = 'pending';
      }
      
      const response = await adminService.getUserManagement({
        page: currentPage,
        page_size: pageSize,
        search: searchQuery || undefined,
        status
      });
      
      setUsers(response.users);
      setTotalCount(response.total_count);
    } catch (error) {
      console.error('Error loading users:', error);
      setLoadError('Failed to load users');
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, viewType]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadDashboardStats();
  }, [loadDashboardStats]);

  // Show error toast when loadError changes
  useEffect(() => {
    if (loadError) {
      showToast(loadError, 'error');
    }
  }, [loadError, showToast]);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowEditUserModal(true);
  };

  const handleToggleAdmin = (user: User) => {
    if (user.isStaff || user.isAdminUser) {
      setConfirmDialog({
        isOpen: true,
        title: 'Remove Admin Role',
        message: `Are you sure you want to remove admin privileges from ${user.firstName} ${user.lastName}?`,
        action: () => performRemoveAdmin(user.id),
        danger: true
      });
    } else {
      setConfirmDialog({
        isOpen: true,
        title: 'Grant Admin Role',
        message: `Are you sure you want to grant admin privileges to ${user.firstName} ${user.lastName}?`,
        action: () => performMakeAdmin(user.id)
      });
    }
  };

  const handleDeleteUser = (user: User) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to permanently delete ${user.firstName} ${user.lastName}? This action cannot be undone.`,
      action: () => performDeleteUser(user.id),
      danger: true
    });
  };

  /**
   * Invalidates a password, ends every session, and emails a reset code.
   *
   * The confirmation spells out all three, because only the first is obvious and the
   * second is the one that matters: changing a password by hand does not sign anybody
   * out, so an exposed account stays reachable for up to a week on an existing refresh
   * token. Whoever clicks this needs to know they are getting the whole thing.
   */
  const handleForcePasswordReset = (user: User) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Reset this password',
      message:
        // Email with a name fallback. It read "undefined will be signed out..." because
        // this endpoint serialised the User entity, whose email is @JsonIgnore'd. The
        // backend sends it now, and the fallback stays because a confirmation naming the
        // wrong account is worse than one naming it awkwardly.
        `${user.email || `${user.firstName} ${user.lastName}`.trim() || user.username} ` +
        `will be signed out on every device, their current password will ` +
        `stop working, and they will be emailed a code valid for one hour to set a new ` +
        `one. Use this when an account's credentials may have been exposed. You cannot ` +
        `undo it, but they can recover with Forgot password.`,
      action: () => performForcePasswordReset(user),
      danger: true
    });
  };

  const performForcePasswordReset = async (user: User) => {
    try {
      setActionLoading(true);
      await adminService.forcePasswordReset(user.id);
      showToast('Password invalidated, sessions revoked, and a reset code emailed', 'success');
      loadUsers();
    } catch (_error) {
      showToast('Failed to force a password reset', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const [staffModalFor, setStaffModalFor] = useState<User | null>(null);

  /**
   * Staff is separate from admin, and this is the only way to grant it.
   *
   * There was no way at all before: makeUserAdmin set isStaff and isAdminUser together,
   * so the two were one thing. Removing the role is a confirm rather than a modal because
   * it takes no answer.
   */
  const handleToggleStaff = (user: User) => {
    if (user.isStaff) {
      setConfirmDialog({
        isOpen: true,
        title: 'Remove staff role',
        message:
          `${user.email} will lose access to the admin panel and their position on the ` +
          `team will be cleared. Their account and anything they created stay as they are.`,
        action: () => performRemoveStaff(user),
        danger: true
      });
      return;
    }
    setStaffModalFor(user);
  };

  const performGrantStaff = async (user: User, position: string) => {
    try {
      setActionLoading(true);
      await adminService.makeUserStaff(user.id, position);
      showToast('Staff role granted', 'success');
      setStaffModalFor(null);
      loadUsers();
    } catch (_error) {
      showToast('Failed to grant the staff role', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const performRemoveStaff = async (user: User) => {
    try {
      setActionLoading(true);
      await adminService.removeUserStaff(user.id);
      showToast('Staff role removed', 'success');
      loadUsers();
    } catch (_error) {
      showToast('Failed to remove the staff role', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performMakeAdmin = async (userId: string) => {
    try {
      setActionLoading(true);
      await adminService.makeUserAdmin(userId);
      showToast('User granted admin privileges', 'success');
      loadUsers();
      loadDashboardStats(); // Refresh stats after admin change
    } catch (_error) {
      showToast('Failed to grant admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performRemoveAdmin = async (userId: string) => {
    try {
      setActionLoading(true);
      await adminService.removeUserAdmin(userId);
      showToast('Admin privileges removed', 'success');
      loadUsers();
      loadDashboardStats(); // Refresh stats after admin change
    } catch (_error) {
      showToast('Failed to remove admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performDeleteUser = async (userId: string) => {
    try {
      setActionLoading(true);
      await adminService.deleteUser(userId);
      showToast('User deleted successfully', 'success');
      loadUsers();
      loadDashboardStats(); // Refresh stats after user deletion
    } catch (_error) {
      showToast('Failed to delete user', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  function handleBulkMakeAdmin(userIds: string[]) {
    setConfirmDialog({
      isOpen: true,
      title: 'Grant Admin Role to Multiple Users',
      message: `Are you sure you want to grant admin privileges to ${userIds.length} selected users?`,
      action: () => performBulkMakeAdmin(userIds)
    });
  }

  function handleBulkRemoveAdmin(userIds: string[]) {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Admin Role from Multiple Users',
      message: `Are you sure you want to remove admin privileges from ${userIds.length} selected users?`,
      action: () => performBulkRemoveAdmin(userIds),
      danger: true
    });
  }

  function handleBulkDelete(userIds: string[]) {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Multiple Users',
      message: `Are you sure you want to permanently delete ${userIds.length} selected users? This action cannot be undone.`,
      action: () => performBulkDelete(userIds),
      danger: true
    });
  }

  const performBulkMakeAdmin = async (userIds: string[]) => {
    try {
      setActionLoading(true);
      // Since bulkMakeUsersAdmin doesn't exist, we'll make individual requests
      for (const userId of userIds) {
        await adminService.makeUserAdmin(userId);
      }
      showToast(`Admin privileges granted to ${userIds.length} users`, 'success');
      setSelectedUserIds([]);
      loadUsers();
      loadDashboardStats(); // Refresh stats after bulk admin change
    } catch (_error) {
      showToast('Failed to grant admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performBulkRemoveAdmin = async (userIds: string[]) => {
    try {
      setActionLoading(true);
      for (const userId of userIds) {
        await adminService.removeUserAdmin(userId);
      }
      showToast(`Admin privileges removed from ${userIds.length} users`, 'success');
      setSelectedUserIds([]);
      loadUsers();
      loadDashboardStats(); // Refresh stats after bulk admin change
    } catch (_error) {
      showToast('Failed to remove admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const handleCreateUser = async (userData: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    password: string;
  }) => {
    try {
      await adminService.createUser(userData);
      showToast('User created successfully', 'success');
      loadUsers();
      loadDashboardStats(); // Refresh stats after creating user
    } catch (_error) {
      showToast('Failed to create user', 'error');
      throw _error; // Re-throw so modal can handle it
    }
  };

  const handleUpdateUser = async (userData: {
    id: string;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    is_admin: boolean;
  }) => {
    try {
      const currentUser = editingUser;
      const wasAdmin = currentUser?.isStaff || currentUser?.isAdminUser || false;
      
      // Update basic user info
      await adminService.updateUser(userData.id, {
        username: userData.username,
        email: userData.email,
        first_name: userData.first_name,
        last_name: userData.last_name,
        is_admin: userData.is_admin
      });

      // Handle admin status change if needed
      if (userData.is_admin !== wasAdmin) {
        if (userData.is_admin) {
          await adminService.makeUserAdmin(userData.id);
        } else {
          await adminService.removeUserAdmin(userData.id);
        }
      }

      showToast('User updated successfully', 'success');
      loadUsers();
      loadDashboardStats(); // Refresh stats after updating user
    } catch (_error) {
      showToast('Failed to update user', 'error');
      throw _error; // Re-throw so modal can handle it
    }
  };

  const performBulkDelete = async (userIds: string[]) => {
    try {
      setActionLoading(true);
      // Since bulkDeleteUsers doesn't exist, we'll make individual requests
      for (const userId of userIds) {
        await adminService.deleteUser(userId);
      }
      showToast(`${userIds.length} users deleted successfully`, 'success');
      setSelectedUserIds([]);
      loadUsers();
      loadDashboardStats(); // Refresh stats after bulk user deletion
    } catch (_error) {
      showToast('Failed to delete users', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{getPageTitle()}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {viewType === 'admin' ? 'Manage admin users and their privileges' : 
             viewType === 'pending' ? 'Users waiting for email verification' : 
             'Manage platform users, roles, and permissions'}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          <Link
            to="/admin/users/admins"
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Admin Users</span>
            <span className="sm:hidden">Admins</span>
          </Link>
          
          <Link
            to="/admin/users/pending"
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <UserX className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Pending Verification</span>
            <span className="sm:hidden">Pending</span>
          </Link>
          
          <button 
            onClick={() => setShowAddUserModal(true)}
            className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 bg-blue-500 rounded-md">
                <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>
            <div className="ml-3 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Users</dt>
                <dd className="text-sm sm:text-lg font-medium text-gray-900">
                  {statsLoading ? 'Loading...' : (dashboardStats?.total_users ?? 0).toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 bg-green-500 rounded-md">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>
            <div className="ml-3 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Verified Users</dt>
                <dd className="text-sm sm:text-lg font-medium text-gray-900">
                  {statsLoading ? 'Loading...' : (dashboardStats?.verified_users ?? 0).toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 bg-orange-500 rounded-md">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>
            <div className="ml-3 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Admin Users</dt>
                <dd className="text-sm sm:text-lg font-medium text-gray-900">
                  {statsLoading ? 'Loading...' : (dashboardStats?.admin_users ?? 0).toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 bg-yellow-500 rounded-md">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
            </div>
            <div className="ml-3 sm:ml-5 w-0 flex-1">
              <dl>
                <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">Pending Verification</dt>
                <dd className="text-sm sm:text-lg font-medium text-gray-900">
                  {statsLoading ? 'Loading...' : (dashboardStats?.pending_users ?? 0).toLocaleString()}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        pagination={{
          current: currentPage,
          total: totalCount,
          pageSize: pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: setPageSize
        }}
        selection={{
          selectedKeys: selectedUserIds,
          onChange: setSelectedUserIds,
          getRowKey: (record) => (record as User).id
        }}
        actions={actions}
        onSearch={setSearchQuery}
        searchPlaceholder="Search users by name, email, or username..."
        bulkActions={bulkActions}
        emptyText="No users found"
      />

      {/* Confirm Dialog */}
      <StaffRoleModal
        isOpen={staffModalFor !== null}
        personLabel={staffModalFor?.email || ''}
        initialPosition={staffModalFor?.teamPosition || null}
        loading={actionLoading}
        onCancel={() => setStaffModalFor(null)}
        onConfirm={(position) => staffModalFor && performGrantStaff(staffModalFor, position)}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        danger={confirmDialog.danger}
        loading={actionLoading}
      />

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSubmit={handleCreateUser}
        loading={actionLoading}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={showEditUserModal}
        onClose={() => {
          setShowEditUserModal(false);
          setEditingUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={editingUser}
        loading={actionLoading}
      />
    </div>
  );
};

export default UserManagement;